import { NavigateFunction } from 'react-router-dom';

export class NavigationService {
  private navigate: NavigateFunction | null = null;

  /**
   * Initialize the service with navigate function and current path getter
   */
  setNavigate(navigate: NavigateFunction): void {
    this.navigate = navigate;
  }

  /**
   * Scroll to top of the page
   */
  private scrollToTop(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  /**
   * Navigate to path or just scroll if already there
   */
  private navigateOrScroll(
    path: string,
    options?: { replace?: boolean }
  ): void {
    if (!this.navigate) {
      console.warn('NavigationService: navigate function not initialized');
      return;
    }

    this.navigate(path, options);
  }

  // ===============================
  // WORKSPACE ROUTES
  // ===============================

  /**
   * Navigate to workspace home
   */
  toWorkspace(workspaceSlug: string): void {
    this.navigateOrScroll(`/w/${workspaceSlug}`);
  }

  /**
   * Navigate to calendar view
   */
  toCalendar(
    workspaceSlug: string,
    view: 'week' | 'month' | 'day',
    date: string,
    options?: { replace?: boolean }
  ): void {
    const path = `/w/${workspaceSlug}/calendar/${view}/${date}`;
    this.navigateOrScroll(path, options);
  }

  /**
   * Navigate to training details
   */
  toTraining(workspaceSlug: string, trainingId: string): void {
    this.navigateOrScroll(`/w/${workspaceSlug}/training/${trainingId}`);
  }

  /**
   * Navigate to team page
   */
  toTeam(workspaceSlug: string): void {
    this.navigateOrScroll(`/w/${workspaceSlug}/team`);
  }

  /**
   * Navigate to user profile
   */
  toProfile(workspaceSlug: string, userId?: string): void {
    const path = userId
      ? `/w/${workspaceSlug}/team/${userId}`
      : `/w/${workspaceSlug}/profile`;
    this.navigateOrScroll(path);
  }

  /**
   * Navigate to billing page
   */
  toBilling(workspaceSlug: string): void {
    this.navigateOrScroll(`/w/${workspaceSlug}/billing`);
  }

  /**
   * Navigate to settings
   */
  toSettings(workspaceSlug: string): void {
    this.navigateOrScroll(`/w/${workspaceSlug}/settings`);
  }

  // ===============================
  // SPECIAL ROUTES
  // ===============================

  /**
   * Navigate to 404 page
   */
  to404(): void {
    this.navigateOrScroll('/404');
  }

  /**
   * Go back in history
   */
  goBack(): void {
    if (!this.navigate) {
      console.warn('NavigationService: navigate function not initialized');
      return;
    }
    this.navigate(-1);
  }

  // ===============================
  // CALENDAR SPECIFIC NAVIGATION
  // ===============================

  /**
   * Navigate to specific day view
   */
  toDay(
    workspaceSlug: string,
    date: string,
    options?: { replace?: boolean }
  ): void {
    this.toCalendar(workspaceSlug, 'day', date, options);
  }

  /**
   * Navigate to week view
   */
  toWeek(
    workspaceSlug: string,
    date: string,
    options?: { replace?: boolean }
  ): void {
    this.toCalendar(workspaceSlug, 'week', date, options);
  }

  /**
   * Navigate to month view
   */
  toMonth(
    workspaceSlug: string,
    date: string,
    options?: { replace?: boolean }
  ): void {
    this.toCalendar(workspaceSlug, 'month', date, options);
  }

  // ===============================
  // RAW NAVIGATION (without automatic scroll)
  // ===============================

  /**
   * Raw navigate without scroll - use sparingly
   */
  navigateRaw(path: string, options?: { replace?: boolean }): void {
    if (!this.navigate) {
      console.warn('NavigationService: navigate function not initialized');
      return;
    }
    this.navigate(path, options);
  }
}
